/**
 * Diagram Fork Model
 *
 * Tracks the relationship between forked diagrams and their originals,
 * enabling reconciliation (merge) operations similar to Git branches.
 */

// =============================================================================
// Fork Metadata Types
// =============================================================================

/**
 * Metadata stored for a forked diagram.
 * This enables tracking where the diagram came from and when.
 */
export interface DiagramForkMetadata {
  /** The ID of the diagram this was forked from */
  forkedFromDiagramId: string;

  /** The folder ID containing the original diagram */
  forkedFromFolderId?: string;

  /** The name of the original diagram at fork time */
  forkedFromDiagramName: string;

  /** Base64-encoded Yjs state vector at the time of fork */
  forkedAtStateVector: string;

  /** Timestamp when the fork was created */
  forkedAtTimestamp: Date;

  /** Name of the scenario that was used to create this fork */
  scenarioName: string;

  /** Description of changes included in the fork */
  scenarioDescription?: string;

  /** Number of changes in the scenario at fork time */
  changeCount: number;

  /**
   * Base64-encoded Yjs snapshot of the original diagram at fork time.
   * Used for accurate compare/reconcile even after the original evolves.
   */
  forkedAtSnapshot?: string;
}

/**
 * Stored format for fork metadata (dates as strings for JSON serialization)
 */
export interface StoredForkMetadata {
  forkedFromDiagramId: string;
  forkedFromFolderId?: string;
  forkedFromDiagramName: string;
  forkedAtStateVector: string;
  forkedAtTimestamp: string;
  scenarioName: string;
  scenarioDescription?: string;
  changeCount: number;
  forkedAtSnapshot?: string;
}

// =============================================================================
// Diff Types for Reconciliation
// =============================================================================

/**
 * Represents a single change detected between two diagram states
 */
export interface DiagramChange {
  type: 'added' | 'removed' | 'modified' | 'moved';
  nodeId: string;
  nodeName: string;
  nodeType: string;
  details?: string;
  /** For modified nodes, what fields changed */
  changedFields?: string[];
  /** For moved nodes, old and new parent */
  oldParentId?: string | null;
  newParentId?: string | null;
}

/**
 * Result of comparing two diagram states
 */
export interface DiagramDiff {
  /** Changes made in the original diagram since fork */
  originalChanges: DiagramChange[];

  /** Changes made in the forked diagram since fork */
  forkedChanges: DiagramChange[];

  /** Nodes that were modified in both diagrams (potential conflicts) */
  conflicts: ConflictInfo[];

  /** Whether reconciliation can proceed automatically */
  canAutoReconcile: boolean;
}

/**
 * Information about a conflict between original and forked changes
 */
export interface ConflictInfo {
  nodeId: string;
  nodeName: string;
  originalChange: DiagramChange;
  forkedChange: DiagramChange;
  /** Suggested resolution */
  suggestedResolution: 'keep-original' | 'keep-forked' | 'manual';
}

// =============================================================================
// Reconciliation Types
// =============================================================================

/**
 * Options for reconciliation operation
 */
export interface ReconcileOptions {
  /** Direction of reconciliation */
  direction: 'pull' | 'push';

  /** How to handle conflicts */
  conflictResolution: 'keep-original' | 'keep-forked' | 'manual';

  /** Specific conflict resolutions (nodeId -> resolution) */
  manualResolutions?: Map<string, 'keep-original' | 'keep-forked'>;
}

/**
 * Result of a reconciliation operation
 */
export interface ReconcileResult {
  success: boolean;
  appliedChanges: number;
  skippedChanges: number;
  unresolvedConflicts: ConflictInfo[];
  error?: string;
}

// =============================================================================
// Storage Helpers
// =============================================================================

const FORK_STORAGE_KEY = 'creately_diagram_forks';

/**
 * Load fork metadata for a diagram
 */
export function loadForkMetadata(diagramId: string): DiagramForkMetadata | null {
  try {
    const stored = localStorage.getItem(`${FORK_STORAGE_KEY}_${diagramId}`);
    if (!stored) return null;

    const parsed = JSON.parse(stored) as StoredForkMetadata;
    return {
      ...parsed,
      forkedAtTimestamp: new Date(parsed.forkedAtTimestamp),
    };
  } catch {
    return null;
  }
}

/**
 * Save fork metadata for a diagram
 */
export function saveForkMetadata(diagramId: string, metadata: DiagramForkMetadata): boolean {
  try {
    const toStore: StoredForkMetadata = {
      ...metadata,
      forkedAtTimestamp: metadata.forkedAtTimestamp.toISOString(),
    };
    localStorage.setItem(`${FORK_STORAGE_KEY}_${diagramId}`, JSON.stringify(toStore));
    return true;
  } catch (e) {
    if (e instanceof DOMException && e.name === 'QuotaExceededError') {
      console.warn('[DiagramFork] Storage quota exceeded');
    }
    return false;
  }
}

/**
 * Delete fork metadata for a diagram
 */
export function deleteForkMetadata(diagramId: string): void {
  localStorage.removeItem(`${FORK_STORAGE_KEY}_${diagramId}`);
}

/**
 * Get all forked diagrams that originated from a specific diagram
 */
export function getForksOfDiagram(originalDiagramId: string): string[] {
  const forks: string[] = [];

  // Scan localStorage for fork metadata pointing to this diagram
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(FORK_STORAGE_KEY)) {
      try {
        const stored = localStorage.getItem(key);
        if (stored) {
          const parsed = JSON.parse(stored) as StoredForkMetadata;
          if (parsed.forkedFromDiagramId === originalDiagramId) {
            const diagramId = key.replace(`${FORK_STORAGE_KEY}_`, '');
            forks.push(diagramId);
          }
        }
      } catch {
        // Skip invalid entries
      }
    }
  }

  return forks;
}

/**
 * Check if a diagram is a fork
 */
export function isFork(diagramId: string): boolean {
  return loadForkMetadata(diagramId) !== null;
}
